import {ComponentType, ReactNode, useState} from 'react';
import {
  BodySite,
  BodySiteId,
  Condition,
  ScoringSystemItem,
  ScoringSystem,
  StepLabel,
  Stepper
} from '@legit-health/ui';
import {ConditionId} from '@legit-health/ui';
import {Box, Container, Step} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useCompany} from '@/shared/contexts/UserContext/useUserContext';
import {AnamnesisQuestion} from '@/shared/interfaces/AnamnesisQuestion';
import DiagnosisSupportForm from './components/DiagnosisSupportForm';
import FollowUpForm from './components/FollowUpForm';
import useSteps from './hooks/useSteps';
import SelectPathologyStep, {Value as PathologyStepValue} from './steps/SelectPathologyStep';
import StepContainer from './steps/StepContainer';
import {BasePatient, ConditionsInfo, CreateDiagnosticReportResponse} from './types';

export default function DiagnosticReportForm({
  conditions,
  bodySites,
  conditionsInfo,
  conditionId = null,
  extraPostData,
  visitIdentifierComponent,
  patient,
  onSuccess,
  anamnesisQuestions,
  renderItemLabel,
  withTemporaryImages
}: {
  conditions: Condition[];
  bodySiteId?: BodySiteId | null;
  bodySites: BodySite[];
  conditionId?: ConditionId | null;
  conditionsInfo: ConditionsInfo;
  extraPostData?: (conditionId: string | null) => Record<string, any>;
  visitIdentifierComponent?: ComponentType<{
    onChange: (visitIdentifier: string) => void;
    value: string;
  }>;
  noScoringSystems: ReactNode;
  patient: BasePatient;
  onSuccess: (values: CreateDiagnosticReportResponse) => void;
  anamnesisQuestions: AnamnesisQuestion[];
  renderItemLabel?: (options: {
    item: ScoringSystemItem;
    scoringSystem: ScoringSystem;
    bodySite: BodySite;
  }) => string;
  withTemporaryImages: boolean;
}) {
  const company = useCompany();
  const {t} = useTranslation(['ui', 'shared/common']);
  const [currentStep, setCurrentStep] = useState(0);
  const steps = useSteps({t, company});
  const [pathologyStepValue, setPathologyStepValue] = useState<PathologyStepValue>({
    bodySiteId: null,
    conditionId: conditionId ? `${conditionId}` : null,
    visitIdentifier: '',
    pathologyId: null,
    fullBody: false
  });

  function handleContinue(value: PathologyStepValue) {
    setPathologyStepValue(value);
    setCurrentStep(1);
  }

  let node = null;
  if (pathologyStepValue?.bodySiteId && currentStep > 0) {
    node = (
      <DiagnosisSupportForm
        anamnesisQuestions={anamnesisQuestions}
        bodySiteId={pathologyStepValue?.bodySiteId}
        currentStep={currentStep}
        isForClinicalTrials={company.isForClinicalTrials}
        onSuccess={onSuccess}
        patient={patient}
        renderItemLabel={renderItemLabel}
        setCurrentStep={setCurrentStep}
        steps={steps}
        t={t}
        visitIdentifier={pathologyStepValue.visitIdentifier}
        withTemporaryImages={withTemporaryImages}
      />
    );
  } else if (pathologyStepValue?.conditionId && currentStep > 0) {
    const conditionInfo = conditionsInfo[pathologyStepValue.conditionId] ?? null;
    node = (
      <FollowUpForm
        anamnesisQuestions={anamnesisQuestions}
        conditionId={pathologyStepValue?.conditionId}
        conditionInfo={conditionInfo}
        currentStep={currentStep}
        extraPostData={extraPostData}
        isForClinicalTrials={company.isForClinicalTrials}
        onSuccess={onSuccess}
        patient={patient}
        renderItemLabel={renderItemLabel}
        setCurrentStep={setCurrentStep}
        steps={steps}
        t={t}
        visitIdentifier={pathologyStepValue.visitIdentifier}
        withTemporaryImages={withTemporaryImages}
        conditions={conditions}
        fullBody={pathologyStepValue.fullBody}
        pathologyId={pathologyStepValue.pathologyId}
      />
    );
  } else {
    node = (
      <SelectPathologyStep
        bodySites={bodySites}
        conditions={conditions.filter((c) => c.canBeTracked)}
        visitIdentifierComponent={visitIdentifierComponent}
        onContinue={handleContinue}
        t={t}
        value={pathologyStepValue}
        patient={patient}
        isForClinicalTrials={company.isForClinicalTrials}
      />
    );
  }

  return (
    <Container
      maxWidth="xl"
      disableGutters
      sx={{
        pt: {
          xs: 2,
          lg: 4
        }
      }}
    >
      <Box
        sx={{
          display: {
            xs: 'none',
            lg: 'block'
          }
        }}
      >
        <Box
          sx={{
            mb: 12
          }}
        >
          <Stepper activeStep={currentStep}>
            {steps.map(({label, icon}, index) => (
              <Step key={label}>
                <StepLabel isActive={index === currentStep} label={label} icon={icon} />
              </Step>
            ))}
          </Stepper>
        </Box>
      </Box>
      <StepContainer>{node}</StepContainer>
    </Container>
  );
}
